export const THEME_SLUGS = [
  'protection',
  'peace-and-blessings',
  'abundance',
  'calm',
  'zodiac',
  'meaningful-gifts',
] as const;

export type ThemeSlug = typeof THEME_SLUGS[number];

export type DesignerTheme = {
  slug: ThemeSlug | null;
  name: string;
  title: string;
  description: string;
  crest: string;
  frame: string;
  accent: string;
};

export const NEUTRAL_THEME: DesignerTheme = {
  slug: null,
  name: 'Open design',
  title: 'Shape a bracelet story',
  description: 'Begin without a theme, and let the balance of the arrangement guide you.',
  crest: '✦',
  frame: '#6b405d',
  accent: '#ead9cf',
};

export const DESIGNER_THEMES: Record<ThemeSlug, DesignerTheme> = {
  protection: {
    slug: 'protection', name: 'Protection', title: 'Design with protection', crest: '◈',
    description: 'A symbolic frame for boundaries, steadiness, and choices that feel grounded.',
    frame: '#594158', accent: '#d7beb5',
  },
  'peace-and-blessings': {
    slug: 'peace-and-blessings', name: 'Peace & Blessings', title: 'Design with peace & blessings', crest: '☾',
    description: 'A gentle frame for hope, gratitude, and the calm you want to remember.',
    frame: '#7c626d', accent: '#d9e0dd',
  },
  abundance: {
    slug: 'abundance', name: 'Abundance', title: 'Design with abundance', crest: '✺',
    description: 'A symbolic frame for noticing possibility, generosity, and what is already present.',
    frame: '#8a5e38', accent: '#e3b968',
  },
  calm: {
    slug: 'calm', name: 'Calm', title: 'Design with calm', crest: '≈',
    description: 'A quiet frame for creating a pause and returning gently to yourself.',
    frame: '#59636d', accent: '#b7c4c1',
  },
  zodiac: {
    slug: 'zodiac', name: 'Zodiac', title: 'Design with a celestial frame', crest: '✧',
    description: 'A symbolic frame for identity, season, and stories written around the stars.',
    frame: '#3e3457', accent: '#a79bb4',
  },
  'meaningful-gifts': {
    slug: 'meaningful-gifts', name: 'Meaningful Gifts', title: 'Design a meaningful gift', crest: '♡',
    description: 'A thoughtful frame for a person, a moment, or a message you want to honor.',
    frame: '#8b6954', accent: '#e5d6c7',
  },
};

export const THEME_CHOICES: DesignerTheme[] = [
  NEUTRAL_THEME,
  ...THEME_SLUGS.map((slug) => DESIGNER_THEMES[slug]),
];

export function parseThemeSlug(value: string | null): ThemeSlug | null {
  return value !== null && (THEME_SLUGS as readonly string[]).includes(value) ? value as ThemeSlug : null;
}

export function getDesignerTheme(slug: ThemeSlug | null): DesignerTheme {
  return slug === null ? NEUTRAL_THEME : DESIGNER_THEMES[slug];
}

export function replaceThemeInUrl(href: string, slug: ThemeSlug | null): string {
  const url = new URL(href);
  if (slug === null) url.searchParams.delete('intention');
  else url.searchParams.set('intention', slug);
  return `${url.pathname}${url.search}${url.hash}`;
}
